import { useTranslation } from 'next-i18next'
import { makeStyles, Container } from '@material-ui/core'
import T from '../../home-new/tokens'
import Reveal from '../../home-new/Reveal'

// Hero for /contact?new — matches the design's hero band (Banner.png): a
// glass-office-building scene that bleeds off the RIGHT edge, filling
// roughly the right ~55% of the section; the source image already fades to
// near-white on its own left third, so the h1 + bold lead + muted paragraph
// sit directly on that built-in light wash — same "art bleeds right, copy
// on the light left" composition as HeroBiz (/business) and ServiceHero
// (/service?new).
const useStyles = makeStyles((theme) => ({
  root: {
    // NewShell's <main> reserves 92px of top padding to clear the fixed
    // HeaderNew. On desktop (up('md'), >=960px) this cancels that 92px
    // (marginTop:-92) so the section — and its absolutely-positioned `art`
    // (top:0, height:100%) — fills from the true viewport top, showing the
    // glass-building scene BEHIND the floating header, matching the one
    // consistent hero template used by /new, /business, and the redesigned
    // tracking/about/service heroes. Because `copy` is vertically centered
    // (flex + alignItems:center) rather than absolutely positioned, the same
    // 92px is padded back in (paddingTop:92) so the heading/lead/sub still
    // clear the header instead of sliding under it.
    position: 'relative',
    background: T.gradient.hero,
    minHeight: 'clamp(460px, 34vw, 600px)',
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
    paddingBottom: 40,
    [theme.breakpoints.up('md')]: {
      marginTop: -92,
      paddingTop: 92,
    },
    // True complement of up('md') — MUI v4's down('sm') is "sm and below"
    // (<960px), not <600px, so this (not down('md'), which overlaps up('md')
    // across 960-1279px) is the correct mobile-only override.
    [theme.breakpoints.down('sm')]: {
      minHeight: 'clamp(560px, 118vw, 720px)',
      paddingTop: 28,
      paddingBottom: 36,
    },
  },
  art: {
    position: 'absolute',
    top: 0,
    right: 0,
    height: '100%',
    width: 'auto',
    maxWidth: 'none',
    zIndex: 0,
    pointerEvents: 'none',
    userSelect: 'none',
    // Fade the scene's left edge into the section background so there's no
    // hard seam — the banner already fades to white there, this just makes
    // the join seamless at every viewport width.
    WebkitMaskImage: 'linear-gradient(to right, transparent 0, #000 22%)',
    maskImage: 'linear-gradient(to right, transparent 0, #000 22%)',
    [theme.breakpoints.down('sm')]: {
      position: 'absolute',
      inset: 0,
      display: 'block',
      height: '100%',
      width: '100%',
      maxWidth: 'none',
      margin: 0,
      objectFit: 'cover',
      objectPosition: '78% center',
      WebkitMaskImage: 'none',
      maskImage: 'none',
    },
  },
  container: {
    position: 'relative',
    zIndex: 2,
    width: '100%',
  },
  copy: {
    maxWidth: 560,
    [theme.breakpoints.down('lg')]: { maxWidth: 480 },
    [theme.breakpoints.down('sm')]: {
      maxWidth: 460,
      margin: '0 auto',
      background: 'rgba(255,255,255,0.82)',
      backdropFilter: 'blur(10px)',
      WebkitBackdropFilter: 'blur(10px)',
      border: '1px solid rgba(255,255,255,0.65)',
      borderRadius: 24,
      padding: '28px 24px 30px',
      boxShadow: '0 16px 44px rgba(0,102,204,0.16)',
    },
  },
  heading: {
    fontFamily: T.font.heading,
    fontSize: 48,
    fontWeight: 700,
    lineHeight: 1.15,
    margin: '0 0 18px',
    // Animated blue gradient text — matches the mockup and the same
    // treatment used elsewhere (HeroBiz's "BUSINESS", the tracking/about
    // hero `gradWord`s). Solid `color` fallback first (also the color any
    // browser without background-clip:text support renders), THEN the
    // gradient reveal so unsupported browsers still show solid brand blue
    // instead of invisible text. The copy sits over the banner's own
    // near-white left zone (plus the frosted scrim on mobile), so even the
    // lightest stop (#66D9FF) stays legible.
    color: T.color.brand,
    backgroundImage:
      'linear-gradient(to right, #0066CC, #009EFF, #33C9FF, #66D9FF, #33C9FF, #009EFF, #0066CC)',
    backgroundSize: '300% 100%',
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    animation: '$contactFlow 8s linear infinite',
    '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
    [theme.breakpoints.down('sm')]: { fontSize: 36, margin: '0 0 12px' },
    // Narrower phones only (<600px) — declared after down('sm') so it wins
    // at the overlapping low end via source order.
    [theme.breakpoints.down('xs')]: { fontSize: 28 },
  },
  '@keyframes contactFlow': {
    '0%': { backgroundPosition: '0% 50%' },
    '50%': { backgroundPosition: '100% 50%' },
    '100%': { backgroundPosition: '0% 50%' },
  },
  lead: {
    fontFamily: T.font.heading,
    fontSize: 20,
    fontWeight: 700,
    lineHeight: 1.5,
    color: T.color.ink,
    margin: '0 0 16px',
    [theme.breakpoints.down('sm')]: { fontSize: 17, margin: '0 0 10px' },
  },
  sub: {
    fontFamily: T.font.body,
    fontSize: 16,
    lineHeight: 1.75,
    color: T.color.muted,
    margin: 0,
    maxWidth: 520,
    [theme.breakpoints.down('xs')]: { fontSize: 14.5 },
  },
}))

const ContactHero = () => {
  const classes = useStyles()
  const { t } = useTranslation('contact-us')

  return (
    <section className={classes.root}>
      <Container
        maxWidth={false}
        style={{ maxWidth: T.container }}
        className={classes.container}
      >
        <div className={classes.copy}>
          <Reveal direction="left">
            <h1 className={classes.heading}>{t('new.hero.title')}</h1>
          </Reveal>
          <Reveal direction="left" delay={0.08}>
            <p className={classes.lead}>{t('new.hero.lead')}</p>
          </Reveal>
          <Reveal direction="left" delay={0.15}>
            <p className={classes.sub}>{t('new.hero.sub')}</p>
          </Reveal>
        </div>
      </Container>
      <picture>
        <source srcSet="/static/img/contact-us-new/hero.webp" type="image/webp" />
        <img
          className={classes.art}
          src="/static/img/contact-us-new/hero.png"
          alt={t('new.hero.banner-alt')}
          width={2749}
          height={800}
          fetchpriority="high"
          decoding="async"
        />
      </picture>
    </section>
  )
}

export default ContactHero
